/**
 * pavement.ts — Where the footways alongside each road actually run.
 *
 * A footway follows its road from one end of the map to the other, but it has to
 * **stop at every crossroads**: you can't have a raised kerb running straight
 * across a live carriageway. So each strip is really a set of spans with gaps
 * punched out where perpendicular roads cut through.
 *
 * That's 1D interval subtraction, kept pure and renderer-free here so it can be
 * unit-tested; `render/world.ts` turns the spans into geometry.
 */
import type { Rect } from './level';
import {
  carriagewayRects,
  carriagewayWidth,
  crossingGaps,
  isStreet,
  roadSpan,
  type RoadSegment,
} from './roads';

/** A stretch along a road's long axis, from → to. */
export interface Span {
  from: number;
  to: number;
}

/**
 * Remove `gaps` from the range [start, extent], returning the surviving spans in
 * order. Gaps may overlap, sit outside the range, or swallow it entirely.
 *
 * `start` defaults to 0, which is what every caller wanted while a road always
 * began at the map edge. A road with a span begins wherever it begins, and the
 * subtraction has to happen inside *its* range rather than the map's — outside
 * it there is no footway to punch a hole in.
 */
export function subtractGaps(extent: number, gaps: Span[], start = 0): Span[] {
  if (extent <= start) return [];

  // Clip to the range and drop anything empty, then sort so we can sweep once.
  const clipped = gaps
    .map((g) => ({
      from: Math.max(start, Math.min(g.from, g.to)),
      to: Math.min(extent, Math.max(g.from, g.to)),
    }))
    .filter((g) => g.to > g.from)
    .sort((a, b) => a.from - b.from);

  const spans: Span[] = [];
  let cursor = start;
  for (const gap of clipped) {
    if (gap.from > cursor) spans.push({ from: cursor, to: gap.from });
    // Overlapping gaps just push the cursor further along.
    cursor = Math.max(cursor, gap.to);
  }
  if (cursor < extent) spans.push({ from: cursor, to: extent });
  return spans;
}

/**
 * The spans a footway occupies along one road, given the positions where
 * perpendicular roads cross it and how wide those roads are.
 *
 * @param extent      where the footway ends along this road's axis
 * @param crossings   centre positions of the perpendicular roads
 * @param crossWidth  carriageway width of those roads (the gap punched out)
 * @param start       where the footway begins; 0 (the map edge) by default
 */
export function footwaySpans(
  extent: number,
  crossings: number[],
  crossWidth: number,
  start = 0,
): Span[] {
  const half = crossWidth / 2;
  return subtractGaps(
    extent,
    crossings.map((c) => ({ from: c - half, to: c + half })),
    start,
  );
}

/**
 * Every footway on the map as a world rect — both sides of every road, broken
 * at each crossroads.
 *
 * The **walkable band the whole game is played on**, which makes it the thing
 * most level geometry has to be checked against: nothing solid may stand on it
 * (`pavementClearance.test.ts`), no canopy may hang over it
 * (`treeClearance.test.ts`), and the level generator has to lay a town that
 * respects both (`levelgen/validate.ts`). All three used to derive it from
 * `footwaySpans` with their own copy of this loop, which is three chances for
 * the derivation to drift from the one in `render/world.ts` that actually draws
 * the paving.
 */
export function footwayBands(
  world: { width: number; height: number },
  roads: readonly RoadSegment[],
  roadWidth: number,
  pavementWidth: number,
): Rect[] {
  const bands: Rect[] = [];

  for (const seg of roads) {
    if (!isStreet(seg)) continue;
    const horizontal = seg.orient === 'h';
    const [from, to] = roadSpan(seg, world);
    // This road's own half-width sets where its kerb is; the *crossing* roads'
    // widths set where the paving is broken. Once streets differ, those are two
    // different numbers — see `crossingGaps`.
    const inner = carriagewayWidth(seg, roadWidth) / 2;

    // Per side, because a footway is broken only by a road that actually
    // reaches across it. Two roads that meet at a T break the near pavement and
    // leave the far one whole — you walk along the top of a T without stepping
    // off a kerb. Asking once for both sides put a notch in the paving opposite
    // a road that was never there.
    for (const side of [-1, 1] as const) {
      const gaps = crossingGaps(seg, roads, roadWidth, { streetsOnly: true, side });
      const near = side < 0 ? seg.pos - inner - pavementWidth : seg.pos + inner;

      for (const span of subtractGaps(to, gaps, from)) {
        bands.push(
          horizontal
            ? { x: span.from, y: near, w: span.to - span.from, h: pavementWidth }
            : { x: near, y: span.from, w: pavementWidth, h: span.to - span.from },
        );
      }
    }
  }
  return [
    ...bands,
    ...footwayEndCaps(world, roads, roadWidth, pavementWidth),
    ...laneBands(world, roads, roadWidth, pavementWidth),
  ];
}

/**
 * The cobbled pedestrian lanes, as walkable rects.
 *
 * Part of `footwayBands` rather than a list of its own, because a lane is
 * walkable ground and every rule that cares about walkable ground should see it
 * without being taught to: nothing solid may be built across one, no canopy may
 * hang over one, and a house beside one **fronts onto it**. That last is what
 * gives the inside of a block a frontage at all.
 *
 * A lane is one band the full width of its corridor rather than a pair either
 * side of something — there is no carriageway in it. It still breaks where a
 * street crosses, for the same reason a footway does: the paving cannot run
 * across live tarmac.
 */
export function laneBands(
  world: { width: number; height: number },
  roads: readonly RoadSegment[],
  roadWidth: number,
  laneWidth: number,
): Rect[] {
  const out: Rect[] = [];

  for (const seg of roads) {
    if (isStreet(seg)) continue;
    const horizontal = seg.orient === 'h';
    const [from, to] = roadSpan(seg, world);
    const near = seg.pos - laneWidth / 2;
    const gaps = crossingGaps(seg, roads, roadWidth, { streetsOnly: true });

    for (const span of subtractGaps(to, gaps, from)) {
      out.push(
        horizontal
          ? { x: span.from, y: near, w: span.to - span.from, h: laneWidth }
          : { x: near, y: span.from, w: laneWidth, h: span.to - span.from },
      );
    }
  }
  return out;
}

/**
 * Every piece of ground the road network occupies: carriageways, footways, and
 * the caps across any blind end.
 *
 * The complement of a town's blocks, and the one derivation they are cut from
 * (`blockInteriors` in `systems/yards.ts`). Having a single answer to "where is
 * the road?" is what stops the block interiors from disagreeing with the paving
 * that is actually drawn — the failure mode being a garden planted across a
 * footway, which is invisible in the code and obvious on screen.
 */
export function roadGround(
  world: { width: number; height: number },
  roads: readonly RoadSegment[],
  roadWidth: number,
  pavementWidth: number,
): Rect[] {
  return [
    ...carriagewayRects(world, roads, roadWidth),
    ...footwayBands(world, roads, roadWidth, pavementWidth),
  ];
}

/**
 * The paving across the blind end of a road that stops mid-town.
 *
 * A street that runs off the edge of the map needs no end: the footways on
 * either side simply run off with it. One that **stops** does — without a cap
 * the two footways end in mid-air facing each other across live tarmac, and the
 * walkable band, which is the surface the whole game is played on, is severed
 * exactly where a cul-de-sac ought to let you walk round the head of the street.
 *
 * So each blind end gets one rect spanning the full corridor, a pavement's depth
 * beyond where the carriageway stops. Ends that run into another road are
 * skipped — the pavement there is already the crossing road's.
 */
export function footwayEndCaps(
  world: { width: number; height: number },
  roads: readonly RoadSegment[],
  roadWidth: number,
  pavementWidth: number,
): Rect[] {
  const caps: Rect[] = [];

  for (const seg of roads) {
    if (!isStreet(seg)) continue;
    const horizontal = seg.orient === 'h';
    const extent = horizontal ? world.width : world.height;
    const [from, to] = roadSpan(seg, world);
    // Half this road's corridor: the cap spans its own carriageway and both its
    // footways, so it is measured off the street being capped.
    const reach = carriagewayWidth(seg, roadWidth) / 2 + pavementWidth;

    for (const [at, outward] of [
      [from, -1],
      [to, 1],
    ] as const) {
      // Runs off the map: nothing to cap.
      if (at <= 0 || at >= extent) continue;
      // Runs into another street: that street's own footway is the cap. Judged
      // by how far **that** street reaches, not this one — a back street ending
      // at a high street is capped by the high street's paving, and a high
      // street ending at a back street is not.
      if (
        roads.some(
          (o) =>
            isStreet(o) &&
            o.orient !== seg.orient &&
            Math.abs(o.pos - at) <= carriagewayWidth(o, roadWidth) / 2 + pavementWidth,
        )
      ) {
        continue;
      }

      const along = outward < 0 ? at - pavementWidth : at;
      caps.push(
        horizontal
          ? { x: along, y: seg.pos - reach, w: pavementWidth, h: reach * 2 }
          : { x: seg.pos - reach, y: along, w: reach * 2, h: pavementWidth },
      );
    }
  }
  return caps;
}
